import React, { useContext, useEffect } from 'react';
import moment from 'moment';
import { useQuery } from '@apollo/client';
import { DateFormats } from 'constants/common';
import { CommonDataContext } from 'contexts/CommonDataContext';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { ReportInfo } from './ReportInfo';
import { GenerateReportBtn } from './GenerateReportBtn';
import { GenerateMockReportBtn } from './GenerateMockReportBtn';
import { cobaltIntelligenceApi, LastSecretaryOfStateReportForAccountResponse } from '../api';

interface Props {
  voId: string;
}

export const CobaltIntelligenceCard = ({ voId }: Props) => {
  const { isSuperAdmin } = useContext(CommonDataContext);

  const { loading, data, error, previousData, startPolling, stopPolling } =
    useQuery<LastSecretaryOfStateReportForAccountResponse>(
      cobaltIntelligenceApi.getLastSecretaryOfStateReportForAccount,
      { variables: { input: { voId } } }
    );

  const viewModel = (data ?? previousData)?.cobaltIntelligence?.result?.entity;
  const report = viewModel?.data?.fields ?? [];
  const isGenerateReportAvailable = viewModel ? viewModel.canGenerateReport : true;

  const generateReportTooltip = isGenerateReportAvailable
    ? ''
    : `Next call available after: ${moment(viewModel?.nextAvailableAt).format(DateFormats.DateTimeMedium24Hour)}`;

  useEffect(() => {
    if (!isGenerateReportAvailable) {
      startPolling(60_000);
    } else {
      stopPolling();
    }
  }, [isGenerateReportAvailable, startPolling, stopPolling]);

  return (
    <CardContainer
      title="KYB Report"
      actionBtn={
        <RenderWithCondition condition={!!viewModel}>
          <Stack flexDirection="row" alignItems="center" justifyContent="center" gap="1rem">
            {isSuperAdmin && (
              <GenerateMockReportBtn
                voId={voId}
                tooltip={generateReportTooltip}
                disabled={!isGenerateReportAvailable}
              />
            )}

            <GenerateReportBtn voId={voId} disabled={!isGenerateReportAvailable} tooltip={generateReportTooltip} />
          </Stack>
        </RenderWithCondition>
      }
    >
      <RenderWithFallback
        error={error}
        isLoading={loading && !viewModel}
        fallBack={
          <Stack height="100%" minHeight="10rem" width="100%" alignItems="center" justifyContent="center">
            <CircularProgress />
          </Stack>
        }
      >
        {viewModel && (
          <Stack direction="row" gap="0.5rem" alignItems="center" justifyContent="flex-end" width="100%">
            <Typography color="textDisabled" variant="body2" component="span">
              Last report: {moment(viewModel.createdDateTime).format(DateFormats.DateTimeMedium12Hour)}
            </Typography>

            <Typography color="textDisabled" variant="body2" component="span">
              Requested by: {viewModel.createdBy.fullName}
            </Typography>
          </Stack>
        )}

        <RenderWithCondition
          condition={!!viewModel}
          fallBack={
            <Stack alignItems="center" justifyContent="center" gap="1rem" flexDirection="row">
              {isSuperAdmin && (
                <GenerateMockReportBtn
                  voId={voId}
                  tooltip={generateReportTooltip}
                  disabled={!isGenerateReportAvailable}
                />
              )}

              <GenerateReportBtn voId={voId} disabled={!isGenerateReportAvailable} tooltip={generateReportTooltip} />
            </Stack>
          }
        >
          <ReportInfo report={report} />
        </RenderWithCondition>
      </RenderWithFallback>
    </CardContainer>
  );
};
